import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  EyeIcon,
  ViewOffSlashIcon,
  AlertCircleIcon,
} from "@hugeicons/core-free-icons";
import { useLogin } from "@/features/auth";

const loginSchema = z.object({
  Id: z.string().min(1, "아이디를 입력하세요"),
  Password: z.string().min(1, "비밀번호를 입력하세요"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberIdChecked, setRememberIdChecked] = useState(false);
  const loginMutation = useLogin();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { Id: "", Password: "" },
  });

  const passwordValue = watch("Password");

  useEffect(() => {
    const savedId = localStorage.getItem("savedLoginId");
    if (savedId) {
      setValue("Id", savedId);
      setRememberIdChecked(true);
    }
  }, [setValue]);

  const onSubmit = (data: LoginFormValues) => {
    if (rememberIdChecked) {
      localStorage.setItem("savedLoginId", data.Id);
    } else {
      localStorage.removeItem("savedLoginId");
    }
    loginMutation.mutate(data);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
      <div className="w-full max-w-md rounded-2xl bg-white p-10 shadow-2xl">
        {/* 로고 영역 */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-blue-600">ULA Admin</h1>
          <p className="mt-2 text-sm text-gray-600">
            멀티테넌시 관리 시스템
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* 아이디 */}
          <div>
            <label
              htmlFor="Id"
              className="mb-1.5 block text-sm font-medium text-gray-700"
            >
              아이디
            </label>
            <input
              id="Id"
              type="text"
              placeholder="아이디 입력"
              autoComplete="username"
              className={`w-full rounded-lg border px-4 py-3 text-sm outline-none transition-colors ${
                errors.Id
                  ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              }`}
              {...register("Id")}
            />
            {errors.Id && (
              <p className="mt-1 text-sm text-red-600">{errors.Id.message}</p>
            )}
          </div>

          {/* 비밀번호 */}
          <div>
            <label
              htmlFor="Password"
              className="mb-1.5 block text-sm font-medium text-gray-700"
            >
              비밀번호
            </label>
            <div className="relative">
              <input
                id="Password"
                type={showPassword ? "text" : "password"}
                placeholder="비밀번호 입력"
                autoComplete="current-password"
                className={`w-full rounded-lg border px-4 py-3 pr-11 text-sm outline-none transition-colors ${
                  errors.Password
                    ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                }`}
                {...register("Password")}
              />
              {passwordValue && (
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  <HugeiconsIcon
                    icon={showPassword ? ViewOffSlashIcon : EyeIcon}
                    size={18}
                  />
                </button>
              )}
            </div>
            {errors.Password && (
              <p className="mt-1 text-sm text-red-600">
                {errors.Password.message}
              </p>
            )}
          </div>

          {/* 아이디 저장 */}
          <div className="flex items-center gap-2">
            <input
              id="rememberId"
              type="checkbox"
              checked={rememberIdChecked}
              onChange={(e) => setRememberIdChecked(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor="rememberId"
              className="text-sm text-gray-600 select-none"
            >
              아이디 저장
            </label>
          </div>

          {/* 에러 메시지 */}
          {loginMutation.isError && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 p-4">
              <HugeiconsIcon
                icon={AlertCircleIcon}
                size={16}
                className="shrink-0 text-red-500"
              />
              <p className="text-sm text-red-800">
                {loginMutation.error.message || "로그인에 실패했습니다."}
              </p>
            </div>
          )}

          {/* 로그인 버튼 */}
          <button
            type="submit"
            disabled={loginMutation.isPending}
            className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:bg-blue-400"
          >
            {loginMutation.isPending ? "로그인 중..." : "로그인"}
          </button>
        </form>

        {/* 하단 텍스트 */}
        <p className="mt-6 text-center text-xs text-gray-500">
          멀티테넌시 관리 시스템
        </p>
      </div>
    </div>
  );
}
